import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, Tenant, Location, SignupForm, AuthContextType, UserRole } from '../types';
import { getCurrentTenant, setCurrentTenant, clearCurrentTenant } from '../utils/tenant';
import { authApi, locationsApi, usersApi, locationAccessApi } from '../services/api';
import { buildApiUrl, API_ENDPOINTS } from '../config/api';
import { identifyClarityUser } from '../utils/clarity';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLocationSelector, setShowLocationSelector] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = !!user && !!tenant && !!location;

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Identify user in Clarity when authenticated
  useEffect(() => {
    if (isAuthenticated && user && tenant) {
      identifyClarityUser(user, tenant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, user?.email, tenant?.id, tenant?.name]);

  // Fetch available locations for the user
  const fetchAvailableLocations = async (userId: string) => {
    try {
      console.log('Fetching available locations for user:', userId);
      const response = await locationAccessApi.getUserLocationAccess(userId);
      const accessRecords = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      // Extract location data from the access records
      const locations = accessRecords.map((access: any) => ({
        id: access.locationId,
        tenant_id: access.locationCompanyId || access.location?.companyId || tenant?.id || '',
        name: access.locationName || access.location?.name || 'Unknown Location',
        address: access.locationAddress || access.location?.address,
        phone: access.locationPhone || access.location?.phone,
        email: access.locationEmail || access.location?.email,
        is_active: access.isActive,
        created_at: access.createdAt || '',
        updated_at: access.updatedAt || '',
      })) || [];
      
      console.log('Available locations:', locations);
      setAvailableLocations(locations);
      localStorage.setItem('available_locations', JSON.stringify(locations));
      return locations;
    } catch (error) {
      console.error('Failed to fetch available locations:', error);
      // If we already have locations in localStorage, use those
      const storedLocations = localStorage.getItem('available_locations');
      if (storedLocations) {
        const locations = JSON.parse(storedLocations);
        setAvailableLocations(locations);
      }
      return [];
    }
  };

  const initializeAuth = async () => {
    try {
      // Check for stored auth data
      const storedUser = localStorage.getItem('user');
      const storedTenant = localStorage.getItem('tenant');
      const storedLocation = localStorage.getItem('location');
      const storedToken = localStorage.getItem('auth_token');
      
      console.log('initializeAuth - stored data:', {
        hasUser: !!storedUser,
        hasTenant: !!storedTenant,
        hasLocation: !!storedLocation,
        hasToken: !!storedToken
      });
      
      if (storedUser && storedTenant && storedLocation && storedToken) {
        const userData = JSON.parse(storedUser);
        const tenantData = JSON.parse(storedTenant);
        const locationData = JSON.parse(storedLocation);
        
        console.log('initializeAuth - parsed data:', {
          userData,
          tenantData,
          locationData,
          token: storedToken.substring(0, 20) + '...'
        });
        
        // Restore auth state from localStorage immediately
        // Token validation will happen on the first authenticated request
        console.log('initializeAuth - restoring auth state from localStorage');
        setUser(userData);
        setTenant(tenantData);
        setLocation(locationData);
        
        // Fetch available locations for the user
        await fetchAvailableLocations(userData.id);
        
        // Skip background token verification to avoid premature logout
        // The user will be able to use the app, and if the token is invalid,
        // the API interceptors will handle it on the first authenticated request
        console.log('initializeAuth - skipping background token verification to prevent logout');
        console.log('initializeAuth - user can use the app, API will handle invalid tokens');
      } else {
        console.log('initializeAuth - missing stored data, checking current tenant');
        // Check if we have a current tenant
        const currentTenant = getCurrentTenant();
        if (currentTenant) {
          // Try to load tenant data
          await loadTenantData(currentTenant);
        }
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  const loadTenantData = async (tenantName: string) => {
    try {
      // In a real app, this would be an API call
      // For now, we'll simulate tenant data
      const mockTenant: Tenant = {
        id: '1',
        name: tenantName,
        email: `admin@${tenantName.toLowerCase().replace(/\s+/g, '-')}.com`,
        subscription_plan: 'professional',
        subscription_status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      setTenant(mockTenant);
      localStorage.setItem('tenant', JSON.stringify(mockTenant));
    } catch (error) {
      console.error('Failed to load tenant data:', error);
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      console.log('[AuthContext.login] Starting login process');
      setIsLoading(true);
      
      // Call the REST API
      console.log('[AuthContext.login] Calling authApi.login');
      const response = await authApi.login(email, password);
      console.log('[AuthContext.login] Received response from authApi');
      
      // Handle wrapped response structure: { success: true, data: { token, user, locations } }
      // or direct structure: { token, user, locations }
      const responseData = response?.data || response;
      
      // Extract the actual data from the wrapped response
      // Backend returns: { success: true, data: { token, user, locations } }
      const actualData = responseData?.data || responseData;
      
      if (actualData && actualData.token) {
        const { token, user: userData, locations: locationsData } = actualData;
        
        // Debug: Log the actual API response to understand the structure
        console.log('[AuthContext.login] Login API Response:', { response, responseData, actualData, userData, locationsData });
        
        // Validate that userData exists
        if (!userData) {
          console.error('[AuthContext.login] User data is missing from response:', responseData);
          throw new Error('Login failed - user data not received from server');
        }
        
        // Store the token
        localStorage.setItem('auth_token', token);
        console.log('[AuthContext.login] Token stored in localStorage');
        
        // Create user object from GraphQL response
        const user: User = {
          id: userData.id || '',
          tenant_id: userData.company?.id || '1', // Map company.id to tenant_id
          email: userData.email || '',
          auth_provider_id: userData.id || '',
          first_name: userData.firstName || '',
          last_name: userData.lastName || '',
          role: userData.role || 'receptionist',
          is_active: userData.isActive || true,
          profilePictureUrl: userData.profilePictureUrl || null,
          isKlijentliAdmin: userData.isKlijentliAdmin || false,
          created_at: userData.createdAt || new Date().toISOString(),
          updated_at: userData.updatedAt || new Date().toISOString(),
        };

        // Create tenant object from GraphQL response
        const tenantData: Tenant = {
          id: userData.company?.id || '1',
          name: userData.company?.name || 'Unknown Company',
          email: userData.company?.email || userData.email,
          subscription_plan: 'professional',
          subscription_status: 'active',
          created_at: userData.company?.createdAt || new Date().toISOString(),
          updated_at: userData.company?.updatedAt || new Date().toISOString(),
        };

        console.log('[AuthContext.login] Setting user and tenant state');
        setUser(user);
        setTenant(tenantData);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('tenant', JSON.stringify(tenantData));
        
        // Store locations data from login response
        if (locationsData && locationsData.length > 0) {
          localStorage.setItem('available_locations', JSON.stringify(locationsData));
          console.log('[AuthContext.login] Stored locations from login:', locationsData);
        }
        
        // Fetch available locations for the user
        await fetchAvailableLocations(user.id);
        
        // Show location selector after successful login
        console.log('[AuthContext.login] Showing location selector');
        setShowLocationSelector(true);
      } else {
        console.error('[AuthContext.login] Invalid response structure:', response);
        throw new Error('Login failed - invalid response from server');
      }
    } catch (error: any) {
      console.error('[AuthContext.login] Login failed:', error);
      
      // Extract detailed error information
      let errorMessage = 'Login failed';
      
      console.log('[AuthContext.login] Processing error:', { 
        hasResponse: !!error.response,
        hasRequest: !!error.request,
        errorMessage: error.message 
      });
      
      if (error.response) {
        // Server responded with error status (REST API fallback)
        const status = error.response.status;
        const data = error.response.data;
        
        // Handle wrapped error response: { error: "...", message: "..." }
        const errorData = data?.error || data?.data?.error || data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (data?.message) {
          errorMessage = data.message;
        } else if (status === 401) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (status === 400) {
          errorMessage = data?.error || data?.message || 'Invalid request data. Please check your information and try again.';
        } else if (status === 403) {
          errorMessage = data?.error || data?.message || 'Please verify your email before logging in. Check your inbox for a verification link.';
        } else if (status === 429) {
          errorMessage = 'Too many login attempts. Please wait a moment and try again.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Request failed with status ${status}. Please try again.`;
        }
        console.log('[AuthContext.login] Using response error message:', errorMessage);
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        console.log('[AuthContext.login] Using request error message:', errorMessage);
      } else {
        // Use the error message directly (already extracted from GraphQL or thrown)
        errorMessage = error.message || 'An unexpected error occurred. Please try again.';
        console.log('[AuthContext.login] Using direct error message:', errorMessage);
      }
      
      console.log('[AuthContext.login] Throwing error with message:', errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (form: SignupForm): Promise<string> => {
    try {
      setIsLoading(true);
      
      // Prepare signup data (matching REST API expectations)
      const signupData = {
        email: form.email,
        password: form.password,
        firstName: form.first_name,
        lastName: form.last_name,
        tenant_name: form.tenant_name,
      };

      // Call the REST API to create user and tenant
      const response = await authApi.register(signupData);

      // Normalize response structure:
      // - Wrapped success: { success: true, data: {...} }
      // - Nested data: { data: {...} }
      // - Direct payload: { id, email, firstName, ... , message }
      const unwrapResponse = (payload: any): any => {
        if (!payload) return payload;
        if (payload.success && payload.data && typeof payload.data === 'object') {
          return unwrapResponse(payload.data);
        }
        if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
          return unwrapResponse(payload.data);
        }
        return payload;
      };

      const normalizedData = unwrapResponse(response);
      const isPlainObject = (value: unknown): value is Record<string, any> =>
        !!value && typeof value === 'object' && !Array.isArray(value);

      const extractUserLike = (value: any) => {
        if (!isPlainObject(value)) {
          return null;
        }

        const candidate = value.user && isPlainObject(value.user) ? value.user : value;
        if (!isPlainObject(candidate)) {
          return null;
        }

        const hasEmail = typeof candidate.email === 'string';
        const hasName =
          typeof candidate.firstName === 'string' ||
          typeof candidate.first_name === 'string' ||
          typeof candidate.lastName === 'string' ||
          typeof candidate.last_name === 'string';
        const hasId = typeof candidate.id === 'string';

        return hasEmail || hasName || hasId ? candidate : null;
      };

      if (!normalizedData) {
        console.error('Signup failed - empty response payload:', response);
        throw new Error('Signup failed - invalid response from server');
      }

      const parsedSignup = {
        user: extractUserLike(normalizedData),
        token:
          normalizedData?.token ??
          normalizedData?.data?.token ??
          normalizedData?.user?.token,
        locations:
          normalizedData?.locations ??
          normalizedData?.data?.locations ??
          normalizedData?.user?.locations,
        message:
          normalizedData?.message ??
          normalizedData?.data?.message ??
          response?.message ??
          null,
      };

      if (parsedSignup.user || parsedSignup.message) {
        // Only auto-authenticate when we receive a token from the backend
        if (parsedSignup.token && parsedSignup.user) {
          // Store the token
          localStorage.setItem('auth_token', parsedSignup.token);
          
          // Backend returns user data directly with company info
          const userData = parsedSignup.user;
          const firstName = userData.firstName ?? userData.first_name ?? '';
          const lastName = userData.lastName ?? userData.last_name ?? '';
          const companyId =
            userData.company?.id ??
            userData.companyId ??
            userData.tenant_id ??
            userData.tenantId ??
            '1';
          const companyName =
            userData.company?.name ??
            userData.companyName ??
            signupData.tenant_name;
          const companyEmail =
            userData.company?.email ??
            userData.companyEmail ??
            userData.email;
          const createdAt = userData.createdAt ?? userData.created_at ?? new Date().toISOString();
          const updatedAt = userData.updatedAt ?? userData.updated_at ?? new Date().toISOString();
          const lastLogin = userData.lastLogin ?? userData.last_login ?? undefined;
          const phone = userData.phone ?? userData.phone_number ?? undefined;
          const role = (userData.role as UserRole) ?? 'admin';

          const newUser: User = {
            id: userData.id,
            tenant_id: companyId,
            email: userData.email,
            auth_provider_id: 'local',
            first_name: firstName,
            last_name: lastName,
            role,
            is_active: userData.isActive ?? userData.is_active ?? true,
            avatar_url: undefined,
            phone,
            last_login_at: lastLogin,
            created_at: createdAt,
            updated_at: updatedAt,
          };

          const newTenant: Tenant = {
            id: companyId,
            name: companyName,
            email: companyEmail,
            subscription_plan: 'starter',
            subscription_status: 'trial',
            trial_ends_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days
            created_at: userData.company?.createdAt ?? new Date().toISOString(),
            updated_at: userData.company?.updatedAt ?? new Date().toISOString(),
          };
          
          // Set the user and tenant in state
          setUser(newUser);
          setTenant(newTenant);
          
          // Store in localStorage
          localStorage.setItem('user', JSON.stringify(newUser));
          localStorage.setItem('tenant', JSON.stringify(newTenant));
          
          // Store locations data from register response
          if (Array.isArray(parsedSignup.locations) && parsedSignup.locations.length > 0) {
            localStorage.setItem('available_locations', JSON.stringify(parsedSignup.locations));
            console.log('Stored locations from registration:', parsedSignup.locations);
          }
        } else {
          console.log('Signup succeeded without token. Skipping auto-login.', normalizedData);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          localStorage.removeItem('tenant');
        }
        
        // Set current tenant
        setCurrentTenant(form.tenant_name.toLowerCase().replace(/\s+/g, '-'));
        
        // Email verification is handled by the backend automatically
        const successMessage =
          parsedSignup.message ??
          normalizedData?.message ??
          response?.message ??
          'Registration successful! Please check your email to verify your account.';
        console.log('Registration successful:', successMessage);
        
        // Return the success message from the backend
        return successMessage;
      } else {
        console.error('Signup failed - invalid response structure:', response);
        throw new Error('Signup failed - invalid response from server');
      }
    } catch (error: any) {
      console.error('Signup failed - full error:', error);
      console.error('Signup failed - error.response:', error.response);
      console.error('Signup failed - error.response?.data:', error.response?.data);
      
      // Extract detailed error information
      let errorMessage = 'Signup failed';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        console.log('Error response status:', status);
        console.log('Error response data:', data);
        
        // Handle wrapped error response: { error: "...", message: "..." }
        const errorData = data?.error || data?.data?.error || data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (data?.error) {
          errorMessage = data.error;
          console.log('Using data.error:', data.error);
        } else if (data?.message) {
          errorMessage = data.message;
          console.log('Using data.message:', data.message);
        } else if (status === 400) {
          errorMessage = data?.error || data?.message || 'Invalid request data. Please check your information and try again.';
        } else if (status === 409) {
          errorMessage = data?.error || data?.message || 'An account with this email already exists. Please use a different email or try logging in.';
        } else if (status === 422) {
          errorMessage = data?.error || data?.message || 'Validation failed. Please check your input and try again.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Request failed with status ${status}. Please try again.`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        // Something else happened
        errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      }
      
      console.log('AuthContext throwing error:', errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuthData();
  };

  const selectLocation = async (locationId: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Always fetch fresh location data from API to get latest updates
      console.log('Fetching fresh location data from API for:', locationId);
      const response = await locationsApi.getLocation(locationId);
      console.log('Location API response:', response);
      
      // Handle wrapped response structure: { data: locationData } or direct locationData
      const rawLocationData = response?.data || response;
      
      if (rawLocationData) {
        // Map the API response to our Location type (handle camelCase to snake_case)
        const locationData: Location = {
          id: rawLocationData.id,
          tenant_id: rawLocationData.companyId || rawLocationData.tenant_id,
          name: rawLocationData.name,
          address: rawLocationData.address,
          phone: rawLocationData.phone,
          email: rawLocationData.email,
          is_active: rawLocationData.isActive ?? rawLocationData.is_active ?? true,
          created_at: rawLocationData.createdAt || rawLocationData.created_at || new Date().toISOString(),
          updated_at: rawLocationData.updatedAt || rawLocationData.updated_at || new Date().toISOString(),
        };
        
        console.log('Mapped location data:', locationData);
        setLocation(locationData);
        localStorage.setItem('location', JSON.stringify(locationData));
        setShowLocationSelector(false);
        
        // Also refresh available locations to keep cache in sync
        if (user) {
          await fetchAvailableLocations(user.id);
        }
      } else {
        throw new Error('Failed to load location');
      }
    } catch (error) {
      console.error('Failed to select location:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshCurrentLocation = async (): Promise<void> => {
    if (!location?.id) return;
    
    try {
      console.log('Refreshing current location data');
      const response = await locationsApi.getLocation(location.id);
      console.log('Location API response:', response);
      
      // Handle wrapped response structure: { data: locationData } or direct locationData
      const rawLocationData = response?.data || response;
      
      if (rawLocationData) {
        // Map the API response to our Location type (handle camelCase to snake_case)
        const locationData: Location = {
          id: rawLocationData.id,
          tenant_id: rawLocationData.companyId || rawLocationData.tenant_id,
          name: rawLocationData.name,
          address: rawLocationData.address,
          phone: rawLocationData.phone,
          email: rawLocationData.email,
          is_active: rawLocationData.isActive ?? rawLocationData.is_active ?? true,
          created_at: rawLocationData.createdAt || rawLocationData.created_at || new Date().toISOString(),
          updated_at: rawLocationData.updatedAt || rawLocationData.updated_at || new Date().toISOString(),
        };
        
        console.log('Mapped location data:', locationData);
        setLocation(locationData);
        localStorage.setItem('location', JSON.stringify(locationData));
        
        // Also refresh available locations to keep cache in sync
        if (user) {
          await fetchAvailableLocations(user.id);
        }
      }
    } catch (error) {
      console.error('Failed to refresh location:', error);
    }
  };

  const createLocation = async (locationData: Partial<Location>): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Create new location via API
      const newLocation = await locationsApi.createLocation(locationData);
      if (newLocation) {
        setLocation(newLocation);
        localStorage.setItem('location', JSON.stringify(newLocation));
        setShowLocationSelector(false);
      } else {
        throw new Error('Failed to create location');
      }
    } catch (error) {
      console.error('Failed to create location:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserRole = async (userId: string, role: UserRole): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Update user role via API
      const updatedUser = await usersApi.updateUser(userId, { role });
      if (updatedUser) {
        // If updating current user's role, update local state
        if (user && user.id === userId) {
          const updatedUser = { ...user, role };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      } else {
        throw new Error('Failed to update user role');
      }
    } catch (error) {
      console.error('Failed to update user role:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (userData: Partial<User>): Promise<void> => {
    try {
      setIsLoading(true);
      
      // TODO: Implement API call to update user profile
      // For now, we'll simulate the update
      if (user) {
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const acceptInvitation = async (_token: string, _userData: any): Promise<void> => {
    try {
      setIsLoading(true);
      
      // TODO: Implement invitation acceptance when invitation system is ready
      // For now, throw an error to indicate this feature is not yet implemented
      throw new Error('Invitation system is not yet implemented');
      
      // Accept invitation via API
      // const response = await invitationsApi.acceptInvitation(token, userData);
      // if (response.success && response.data) {
      //   const { token: authToken, user: userData, tenant: tenantData, location: locationData } = response.data;
      //   
      //   // Store the auth token
      //   localStorage.setItem('auth_token', authToken);
      //   
      //   // Set user, tenant, and location data
      //   setUser(userData);
      //   setTenant(tenantData);
      //   setLocation(locationData);
      //   
      //   // Store in localStorage
      //   localStorage.setItem('user', JSON.stringify(userData));
      //   localStorage.setItem('tenant', JSON.stringify(tenantData));
      //   localStorage.setItem('location', JSON.stringify(locationData));
      //   
      //   // Set current tenant
      //   setCurrentTenant(tenantData.name.toLowerCase().replace(/\s+/g, '-'));
      // } else {
      //   throw new Error('Failed to accept invitation');
      // }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const sendEmailVerification = async (email: string): Promise<void> => {
    console.log('sendEmailVerification called with email:', email);

    try {
      const requestBody = { email };
      console.log('Request body:', requestBody);

      const response = await fetch(buildApiUrl(API_ENDPOINTS.EMAIL.SEND_VERIFICATION), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      const data = await response.json();
      console.log('Response data:', data);
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Failed to send email verification:', error);
      throw error;
    }
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.EMAIL.SEND_PASSWORD_RESET), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send password reset email');
      }
    } catch (error) {
      console.error('Failed to send password reset email:', error);
      throw error;
    }
  };

  const selectTenant = async (tenantId: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      // In a real app, this would be an API call to get tenant data
      // For now, we'll simulate tenant selection
      const mockTenant: Tenant = {
        id: tenantId,
        name: `Tenant ${tenantId}`,
        email: `admin@tenant${tenantId}.com`,
        subscription_plan: 'professional',
        subscription_status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      setTenant(mockTenant);
      localStorage.setItem('tenant', JSON.stringify(mockTenant));
      setCurrentTenant(mockTenant.name.toLowerCase().replace(/\s+/g, '-'));
    } catch (error) {
      console.error('Failed to select tenant:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearAuthData = () => {
    setUser(null);
    setTenant(null);
    setLocation(null);
    setShowLocationSelector(false);
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    localStorage.removeItem('location');
    localStorage.removeItem('auth_token');
    clearCurrentTenant();
  };

  const value: AuthContextType = {
    user,
    tenant,
    location,
    availableLocations,
    isLoading,
    isAuthenticated,
    showLocationSelector,
    login,
    signup,
    logout,
    selectTenant,
    selectLocation,
    refreshCurrentLocation,
    fetchAvailableLocations,
    createLocation,
    updateUserRole,
    updateUser,
    acceptInvitation,
    sendEmailVerification,
    sendPasswordReset,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
